/**
 * useLessonAvailability — one instructor's lesson grid for one day.
 *
 * Plain `useQuery` over the envelope: `success: false` is data, read from
 * `failure` (docs/conventions/internalapi-envelope.md). Keyed under
 * `lessons.all`, so booking or cancelling a lesson refreshes the grid through
 * the prefix those mutations already invalidate.
 *
 * `cachePresets.frequent`: a slot can be taken by someone else at any moment.
 *
 * @module lessons/hooks/useLessonAvailability
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ApiErrorResult, ApiResult } from '~/lib/apiClient/types';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { appRuntime } from '~/services/appRuntime';
import { getLessonAvailability } from '../api/getLessonAvailability';
import type { ApiLessonAvailability } from '../types';

export interface UseLessonAvailabilityParams {
  clubId: number | undefined;
  instructorId: number | null;
  /** `YYYY-MM-DD`, club-local. */
  date: string | null;
  /** Pass a module-scope or memoized array: it is part of the query key. @unit minutes */
  durations: readonly number[];
  slotInterval: 10 | 15 | 30 | 60;
  enabled?: boolean;
}

export type UseLessonAvailabilityResult = UseQueryResult<ApiResult<ApiLessonAvailability>, Error> & {
  /** The grid, or null until an answer arrives or when the answer was a refusal. */
  availability: ApiLessonAvailability | null;
  /** An answer arrived at least once. */
  hasAnswer: boolean;
  /** The refusal envelope when the latest answer is `success: false`. */
  failure: ApiErrorResult | null;
};

export function useLessonAvailability({
  clubId,
  instructorId,
  date,
  durations,
  slotInterval,
  enabled = true,
}: UseLessonAvailabilityParams): UseLessonAvailabilityResult {
  const tenantHandle = appRuntime.tenantHandle ?? undefined;
  const ready = !!clubId && !!instructorId && !!date && durations.length > 0;

  const query = useQuery({
    queryKey: ready
      ? queryKeys.lessons.availability(clubId, { instructorId, date, durations, slotInterval })
      : queryKeys.disabled.lessons(),
    queryFn: () => {
      if (!clubId || !instructorId || !date || !tenantHandle) {
        throw new Error('clubId, instructorId, date and tenantHandle are required');
      }
      return getLessonAvailability({ clubId, tenantHandle, instructorId, date, durations, slotInterval });
    },
    enabled: enabled && ready && !!tenantHandle,
    ...cachePresets.frequent,
  });

  const result = query.data;

  return {
    ...query,
    availability: result?.success ? result.payload : null,
    hasAnswer: result !== undefined,
    failure: result && !result.success ? result : null,
  };
}
